// Global Context for Toggling Global Components and Modals


import React, {createContext, useCallback, useState} from 'react';
import {Song} from "../utils/types";
import WaveSurfer from "wavesurfer.js";
import useDynamicBreadcrumb from "../hooks/useDynamicBreadcrumb";
import API from "../helpers/network/http";

interface WaveformContextProps {
    waveformRef: React.RefObject<HTMLDivElement> | null;
}

interface Setlist {
    id: number;
    name: string;
    song_list?: any[];
}

export const GlobalContext = createContext({
    isBottomAudioPlayerOpen: false,
    currentSong: {} as Song,
    waveSurfer: {} as WaveSurfer,
    setWaveSurfer: (waveSurfer: any) => {
    },
    setCurrentSong: (song: Song) => {
    },
    audioPlayerFullScreen: false,
    setAudioPlayerFullScreen: (value: boolean) => {},
    toggleBottomAudioPlayer: () => {
    },
    openBottomAudioPlayer: () => {
    },
    closeBottomAudioPlayer: () => {
    },
    waveformRef: {} as WaveformContextProps,
    currentTime: 0, // Track current time
    isPlaying: false, // Global playing state
    setCurrentTime: (time: number) => {},
    setIsPlaying: (playing: boolean) => {},
} as any);

export const GlobalProvider = ({children}: any) => {
    const waveRef = React.useRef<HTMLDivElement>(null);
    useDynamicBreadcrumb();
    const [isBottomAudioPlayerOpen, setIsBottomAudioPlayerOpen] = useState(false);
    const [audioPlayerFullScreen, setAudioPlayerFullScreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentSong, setCurrentSong] = useState({} as Song);
    const [currentPart, setCurrentPart] = useState<string>('Original')
    const [sectionToLoop, setSectionToLoop] = useState<string>('');
    const [currentPartUrl, setCurrentPartUrl] = useState<string>('')
    const [currentPartWaveform, setCurrentPartWaveform] = useState<string>('')
    const [playPart, setPlayPart] = useState<boolean>(false)
    const [currentTime, setCurrentTime] = useState<number>(0)
    const [volume, setVolume] = useState<number>(1); // Default Volume
    const [waveSurfer, setWaveSurfer] = useState<WaveSurfer>({} as any);
    const [waveformRef, setWaveformRef] = useState<React.RefObject<HTMLDivElement> | null>(waveRef);
    const [loading, setLoading] = useState(false);
    const [isWaveformReady, setIsWaveformReady] = useState(false);
    const [isWaveformLoading, setIsWaveformLoading] = useState(false);
    const [waveformProgress, setWaveformProgress] = useState(0);
    const [searchText, setSearchText] = useState('');

    const [setlists, setSetlists] = useState<Setlist[]>([]);
    const [selectedSetlistIds, setSelectedSetlistIds] = useState<Set<number>>(new Set());
    const [initialSelectedSetlistIds, setInitialSelectedSetlistIds] = useState<Set<number>>(new Set());
    const [showSetlistModal, setShowSetlistModal] = useState(false);
    const [setlistSong, setSetlistSong] = useState<Song | null>(null);
    const [selectedSong, setSelectedSong] = useState<Song | null>(null);

    // Memoized function to toggle the bottom audio player
    const toggleBottomAudioPlayer = useCallback(() => {
        // TODO: Add any additional logic needed when toggling the audio player by default
        setIsBottomAudioPlayerOpen(!isBottomAudioPlayerOpen);
    }, [isBottomAudioPlayerOpen])

    const openBottomAudioPlayer = useCallback(() => {
        setIsBottomAudioPlayerOpen((prev) => (prev ? prev : true));
    }, []);

    const closeBottomAudioPlayer = useCallback(() => {
        setIsBottomAudioPlayerOpen((prev) => (prev ? false : prev));
    }, []);

    // Setlist management functions
    const fetchSetlists = useCallback(async () => {
        try {
            const res = await API.get('/setlists');
            setSetlists(res.data.data);
            return res.data.data;
        } catch (e) {
            console.error('Failed to fetch setlists', e);
            return [];
        }
    }, []);


    const openSetlistModal = (song: Song) => {
        setSetlistSong(song);
        setShowSetlistModal(true);
    };

    const closeSetlistModal = () => {
        setSetlistSong(null);
        setShowSetlistModal(false);
    };

    const handleOpenAddToSetlist = (song: Song) => {
        setSelectedSong(song);
        setShowSetlistModal(true);
    }

    return (
        <GlobalContext.Provider
            value={{
                currentSong,
                currentPart,
                sectionToLoop,
                currentPartUrl,
                currentPartWaveform,
                currentTime,
                playPart,
                isBottomAudioPlayerOpen,
                audioPlayerFullScreen,
                isPlaying,
                isWaveformLoading,
                isWaveformReady,
                searchText,
                loading,
                volume,
                setCurrentSong,
                setAudioPlayerFullScreen,
                setCurrentPart,
                setSectionToLoop,
                setCurrentPartUrl,
                setCurrentPartWaveform,
                setCurrentTime,
                setPlayPart,
                setIsPlaying,
                setIsWaveformLoading,
                setIsWaveformReady,
                setLoading,
                setVolume,
                setWaveSurfer,
                setWaveformProgress, // Set the waveformRef to the current waveformRef if we have more than one waveform on a single page - this is so that each waveform will have its own unique waveformRef
                setWaveformRef,
                setSearchText,
                // Setlist related values and functions
                toggleBottomAudioPlayer,
                openBottomAudioPlayer,
                closeBottomAudioPlayer,
                waveSurfer,
                waveformProgress,
                waveformRef,
                setlists,
                selectedSetlistIds,
                initialSelectedSetlistIds,
                showSetlistModal,
                setlistSong,
                selectedSong,
                fetchSetlists,
                openSetlistModal,
                closeSetlistModal,
                setSetlists,
                setSelectedSetlistIds,
                setInitialSelectedSetlistIds,
                setSelectedSong,
                setShowSetlistModal,
                handleOpenAddToSetlist

            }}
        >
            {children}
        </GlobalContext.Provider>
    );
};
